import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { 
  Trophy, 
  Baby, 
  Heart, 
  ShieldCheck, 
  Zap, 
  Stethoscope, 
  AlertCircle, 
  Sparkles, 
  BookOpen, 
  CheckCircle2 
} from "lucide-react";

const PedodonticsDepartment = () => {
  return (
    <ServiceLayout
      title="Department of Pedodontics"
      subtitle="Guinness World Record Holders in Pediatric Dental Health. Providing specialized care for infants, children, and adolescents."
      heroImage="/images/hero/pediatric-dept.jpg"
      images={["/images/guinness-record.jpg", "/images/kids-dental-treatment.jpg"]}
    >
      {/* World Record & Mission Section */}
      <section className="mb-12">
        <div className="bg-yellow-50 border-2 border-yellow-200 p-8 rounded-3xl shadow-sm relative overflow-hidden">
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
            <div className="bg-white p-4 rounded-full shadow-md">
              <Trophy className="w-16 h-16 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-yellow-800 mb-2 uppercase tracking-tight">Guinness World Record Holder</h2>
              <p className="text-gray-700 leading-relaxed font-medium">
                Our institution is globally recognized for conducting the <strong>Largest Dental Health Check Program</strong>, benefiting thousands of children. We are committed to taking dental awareness to the grassroots level through schools and community camps.
              </p>
            </div>
          </div>
          <Trophy className="absolute -bottom-10 -right-10 w-64 h-64 text-yellow-600/5 rotate-12" />
        </div>
      </section>

      {/* Awareness Programs */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-blue-900 mb-8 flex items-center gap-3">
          <BookOpen className="text-blue-600" /> Pediatric Dental Awareness
        </h2>
        <p className="text-gray-700 mb-8">
          We believe early education is the key to lifelong oral health. Our large-scale awareness topics cover:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "Proper tooth brushing techniques",
            "Prevention of dental cavities",
            "Early identification of problems",
            "Effects of sugary foods on teeth",
            "Guidance on safe toothpaste",
            "Chemicals awareness in toothpaste",
            "Importance of regular check-ups",
            "Preventive care for infants",
            "Care tips for growing children"
          ].map((topic, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:border-blue-200 transition">
              <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-xs font-bold text-gray-700 leading-tight">{topic}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Treatment Categories */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Comprehensive Specialized Treatments</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Category 1: Preventive */}
          <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100">
            <h4 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-blue-600" /> Preventive Care
            </h4>
            <ul className="space-y-2">
              {["Fluoride Treatments", "Dental Sealants", "Professional Teeth Cleaning", "Oral Health Counseling"].map((item, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Category 2: Restorative */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200">
            <h4 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-blue-600" /> Restorative Care
            </h4>
            <ul className="space-y-2">
              {["Tooth-Colored Fillings", "Pulpectomy / Pulpotomy (Kid's RCT)", "Stainless Steel Crowns", "Restoration of Decayed Teeth"].map((item, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Category 3: Ortho & Habit */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200">
            <h4 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
              <Stethoscope className="w-6 h-6 text-blue-600" /> Growth & Habit Correction
            </h4>
            <ul className="space-y-2">
              {["Space Maintainers", "Early Bite Correction", "Thumb Sucking Correction", "Mouth Breathing Habit Management"].map((item, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Category 4: Emergency */}
          <div className="bg-red-50 p-6 rounded-2xl border border-red-100">
            <h4 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-red-600" /> Emergency Pediatric Care
            </h4>
            <ul className="space-y-2">
              {["Treatment for Dental Trauma", "Broken / Knocked-Out Teeth", "Emergency Pain Relief", "Monitoring of Jaw Growth"].map((item, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Child-Friendly Approach */}
      <section className="bg-blue-900 text-white p-10 rounded-3xl mb-12 shadow-xl text-center">
        <Sparkles className="w-12 h-12 text-blue-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-6">Gentle & Stress-Free Environment</h2>
        <p className="max-w-3xl mx-auto text-blue-100 leading-relaxed italic">
          "Our pediatric dental department is designed to provide a comfortable and friendly experience. 
          Our specialists are trained to handle young patients with patience and compassion, helping children 
          overcome dental fear and build lifelong confidence."
        </p>
      </section>

      {/* Conclusion */}
      <section className="border-t pt-12">
        <p className="text-gray-700 font-medium">
          If you are looking for pediatric dental care or pedodontic services in Vijayawada or Hyderabad, Dr. Sridhar International Dental Hospitals provide comprehensive care in a safe and comfortable environment.
        </p>
      </section>
    </ServiceLayout>
  );
};

export default PedodonticsDepartment;